import { useState } from 'react';
import { Calendar, User, Edit, Trash2, Clock, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import EditTaskDialog from './EditTaskDialog';
import { toast } from 'sonner';

export const TaskCard = ({ task, onUpdate, onDelete, canEdit = true, canDelete = true, isReadOnly = false }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return <Badge className="bg-destructive/10 text-destructive border-destructive/20">High</Badge>;
      case 'medium':
        return <Badge className="bg-warning/10 text-warning border-warning/20">Medium</Badge>;
      case 'low':
        return <Badge className="bg-muted text-muted-foreground">Low</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  const isOverdue = () => {
    const today = new Date().toISOString().split('T')[0];
    return !!task.dueDate && task.dueDate < today && task.status !== 'done';
  };

  const isDueToday = () => {
    const today = new Date().toISOString().split('T')[0];
    return !!task.dueDate && task.dueDate === today;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  };

  const getStatusColors = () => {
    switch (task.status) {
      case 'done':
        return 'border-l-4 border-l-success bg-success/5';
      case 'in-progress':
        return 'border-l-4 border-l-primary bg-primary/5';
      case 'pending':
      case 'todo':
        return 'border-l-4 border-l-warning bg-warning/5';
      default:
        return 'border-l-4 border-l-muted';
    }
  };

  const handleDelete = () => {
    onDelete(task.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
      <Card className={`p-4 transition-smooth hover:shadow-medium cursor-grab active:cursor-grabbing ${getStatusColors()}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-semibold text-sm pr-2 leading-tight">{task.title}</h4>
          {getPriorityBadge(task.priority)}
        </div>

        {/* Description */}
        {task.description && (
          <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
            {task.description}
          </p>
        )}

        {/* Due Date */}
        {task.dueDate && (
          <div className="flex items-center gap-2 mb-3">
            {isOverdue() ? (
              <>
                <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                <span className="text-xs font-medium text-destructive">
                  Overdue - {formatDate(task.dueDate)}
                </span>
              </>
            ) : (
              <>
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  Due {formatDate(task.dueDate)}
                </span>
              </>
            )}
          </div>
        )}

        {/* Assignee */}
        {(task.assignedToName || task.assignedTo) && (
          <div className="flex items-center gap-2 mb-3 text-xs">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">{task.assignedToName || task.assignedTo}</span>
          </div>
        )}

        {/* Notes */}
        {task.notes && (
          <div className="bg-muted/30 rounded-lg p-2 mb-3">
            <p className="text-xs text-muted-foreground line-clamp-1">📝 {task.notes}</p>
          </div>
        )}

        {/* Actions */}
        {(canEdit || canDelete) && (
          <div className="flex gap-2 pt-3 border-t border-border">
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEditDialog(true);
                }}
                className="flex-1"
                disabled={isReadOnly}
              >
                <Edit className="h-3.5 w-3.5" />
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteDialog(true);
                }}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                disabled={isReadOnly}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Edit Dialog */}
      <EditTaskDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        task={task}
        onUpdateTask={onUpdate}
        weddingId={task.weddingId}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                const isInProgress = task.status === 'in-progress';
                const hasAssignee = task.assignedTo || task.assignedToName;
                const isOverdueTask = isOverdue();
                return (isInProgress || hasAssignee || isOverdueTask)
                  ? "⚠️ Warning: Active Task"
                  : "Delete Task?";
              })()}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {(() => {
                const isInProgress = task.status === 'in-progress';
                const hasAssignee = task.assignedTo || task.assignedToName;
                const isOverdueTask = isOverdue();
                const isDueTodayTask = isDueToday();
                
                if (isInProgress || hasAssignee || isOverdueTask) {
                  return (
                    <>
                      <p>
                        You are about to delete <strong>{task.title}</strong>.
                      </p>
                      <div className="bg-warning/10 border border-warning/20 rounded-md p-3">
                        <p className="text-sm font-medium text-warning-foreground mb-2">
                          ⚠️ Task Details:
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                          {isInProgress && <li>Status: In Progress (active work)</li>}
                          {hasAssignee && <li>Assigned to: {task.assignedToName || task.assignedTo}</li>}
                          {task.dueDate && (
                            <li>
                              Due date: {formatDate(task.dueDate)}
                              {isOverdueTask && <span className="text-destructive"> (Overdue)</span>}
                              {isDueTodayTask && <span className="text-warning"> (Due Today)</span>}
                            </li>
                          )}
                          {task.priority && <li>Priority: {task.priority}</li>}
                          {task.notes && <li>Has notes/details</li>}
                        </ul>
                      </div>
                      <p className="text-sm text-muted-foreground pt-2">
                        This will permanently delete the task and all its details. This action cannot be undone.
                      </p>
                    </>
                  );
                } else {
                  return (
                    <p>
                      Are you sure you want to delete <strong>{task.title}</strong>? This action cannot be undone.
                    </p>
                  );
                }
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(() => {
                const isInProgress = task.status === 'in-progress';
                const hasAssignee = task.assignedTo || task.assignedToName;
                const isOverdueTask = isOverdue();
                return (isInProgress || hasAssignee || isOverdueTask) ? "Delete Anyway" : "Delete Task";
              })()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TaskCard;
